import React, { useState } from 'react';

import { StepProps, track, useMutation } from '@betterment/js-runtime';
import { Text } from '@betterment/samba-react';
import { Box, Button, CenteredWorkflowLayout, Flash, Flex } from '@betterment/style-closet-react';

import { RequestValues } from './Controller';

import { useCustomer } from '../Checking/components/CustomerProvider';
import NotFound from '../Checking/components/NotFound';
import Footer from '../Shared/components/Footer';
import { urlBuilder } from '../Shared/urlBuilder';

const JointAccountInvitationConfirm: React.FC<React.PropsWithChildren<StepProps<RequestValues>>> = ({
  flowState,
  goToNextStep,
  updateValues,
}) => {
  const [shouldShowFlash, setShowFlash] = useState(false);
  const { invitedUser, accountId, requestId, isProspectSecondary } = flowState.values;
  const { refetch } = useCustomer();

  const [createInvitationCompletion, { loading }] = useMutation<
    { accountId: number; isNewAccount: boolean },
    { checkingAccountId: number | null }
  >(urlBuilder.apiV2CheckingJointInvitationRequestCompletion.absolute({ requestId }));

  if (!invitedUser && !isProspectSecondary) {
    return <NotFound />;
  }

  const invitedUserFirstName = invitedUser?.firstName || flowState.values.firstName || '';
  const invitedUserLastName = invitedUser?.lastName || flowState.values.lastName;
  const invitedUserEmail = invitedUser?.email || flowState.values.email;

  const headingCopy = accountId ? 'Send Joint Checking invite?' : 'Create account and send Joint Checking invite?';
  const buttonCopy = accountId ? 'Send invite' : 'Create account and send invite';
  const bodyCopy = accountId
    ? `Until ${invitedUserFirstName} accepts the invite, you can continue to use your Checking account as an individual account.`
    : `Until ${invitedUserFirstName} accepts the invite, you can use your new Checking account as an individual account.`;

  const onContinue = async () => {
    track('ElementClicked', { name: 'CreateJCAccountAndSendInvite', flow: 'OpenJointCheckingPrimary' });
    const { data, errors } = await createInvitationCompletion({ checkingAccountId: accountId });

    if (errors) {
      setShowFlash(true);
      track('ElementViewed', { name: 'CoreproDown', flow: 'OpenJointCheckingPrimary' });
    } else if (data?.accountId) {
      await refetch();
      updateValues({
        accountId: data.accountId,
        isNewAccount: data.isNewAccount,
      });
      goToNextStep();
    }
  };

  return (
    <CenteredWorkflowLayout title="Joint Checking signup" heading={headingCopy}>
      <CenteredWorkflowLayout.Slot name="content">
        {shouldShowFlash && (
          <Box mb="9">
            <Flash variant="error">
              <Text color="text">Something went wrong. Please try again.</Text>
            </Flash>
          </Box>
        )}
        <Box borderTop="1px solid" borderColor="bmtGrey20" pt="6" mt="8">
          <Flex justifyContent="space-between" flexDirection="column">
            <Flex>
              <Text as="p" css={{ marginBottom: 'space.200', _last: { marginBottom: 'space.0' } }}>
                Inviting
              </Text>
            </Flex>
            <Flex>
              <Text
                as="p"
                color="text.weak"
                css={{
                  marginBottom: 'space.0',
                }}
              >
                {invitedUserFirstName} {invitedUserLastName} ({invitedUserEmail})
              </Text>
            </Flex>
          </Flex>
        </Box>
        <Box borderTop="1px solid" borderColor="bmtGrey20" pt="6" mt="6" mb="9">
          <Text as="p" css={{ marginBottom: 'space.200', _last: { marginBottom: 'space.0' } }}>
            The invite expires after 30 days, so make sure {invitedUserFirstName} checks their inbox.
          </Text>
          <Text as="p" css={{ marginBottom: 'space.200', _last: { marginBottom: 'space.0' } }}>
            {bodyCopy}
          </Text>
        </Box>
        <CenteredWorkflowLayout.Actions>
          <Button type="button" px="8" disabled={loading} onClick={onContinue}>
            {buttonCopy}
          </Button>
        </CenteredWorkflowLayout.Actions>
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot name="footer">
        <Footer />
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};

export default JointAccountInvitationConfirm;
